import { Component, EventEmitter, Output, Input, OnInit, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { environment } from '../../../assets/environments/environment';

declare global {
  interface Window {
    grecaptcha: any;
    onRecaptchaLoad: any;
  }
}

@Component({
  selector: 'app-captcha',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="captcha-container mb-4">
      <div *ngIf="showCaptcha" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-gray-700">
          Human Verification <span class="text-red-500">*</span>
        </label>
        <div 
          [id]="captchaId" 
          class="g-recaptcha-container"
          [attr.data-sitekey]="siteKey"
          [attr.data-theme]="theme"
          [attr.data-size]="size">
        </div>
        <div *ngIf="showError" class="text-red-600 text-sm">
          Please complete the CAPTCHA verification
        </div>
      </div>
    </div>
  `,
  styles: [`
    .captcha-container {
      display: flex;
      flex-direction: column;
    }
    
    .g-recaptcha-container {
      margin-top: 0.5rem;
    }
  `]
})
export class CaptchaComponent implements OnInit, OnDestroy {
  @Input() showCaptcha: boolean = true;
  @Input() size: 'compact' | 'normal' = 'normal';
  @Input() theme: 'light' | 'dark' = 'light';
  @Input() showError: boolean = false;
  
  @Output() captchaResolved = new EventEmitter<string>();
  @Output() captchaError = new EventEmitter<void>();
  
  siteKey: string = '';
  captchaId: string = '';
  private widgetId: number | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.captchaId = 'recaptcha-' + Math.random().toString(36).substring(7);
  }

  ngOnInit() {
    this.siteKey = environment.recaptcha?.siteKey || '';
    if (!this.siteKey) {
      console.warn('reCAPTCHA site key not configured in environment');
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.loadRecaptcha();
    }
  }

  ngOnDestroy() {
    if (this.widgetId !== null && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.widgetId);
      } catch (e) {
        console.warn('Error resetting reCAPTCHA:', e);
      }
    }
  }

  private loadRecaptcha() {
    if (document.getElementById('recaptcha-script')) {
      this.renderRecaptcha();
      return;
    }

    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;

    window.onRecaptchaLoad = () => {
      this.renderRecaptcha();
    };

    document.head.appendChild(script);
  }

  private renderRecaptcha() {
    if (!window.grecaptcha || !this.siteKey) return;

    try {
      const element = document.getElementById(this.captchaId);
      if (!element) return;

      this.widgetId = window.grecaptcha.render(this.captchaId, {
        sitekey: this.siteKey,
        theme: this.theme,
        size: this.size,
        callback: (response: string) => this.onCaptchaSuccess(response),
        'error-callback': () => this.onCaptchaError(),
        'expired-callback': () => this.onCaptchaExpired()
      });
    } catch (error) {
      console.error('Error rendering reCAPTCHA:', error);
      this.captchaError.emit();
    }
  }

  private onCaptchaSuccess(response: string) {
    this.captchaResolved.emit(response);
  }

  private onCaptchaError() {
    this.captchaError.emit();
  }

  private onCaptchaExpired() {
    this.captchaError.emit();
  }

  reset() {
    if (this.widgetId !== null && window.grecaptcha) {
      window.grecaptcha.reset(this.widgetId);
    }
  }
}